import { Button, Card, Empty, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import request from "@/api";
import type { OperationLogVo } from "@/entity/mytask/operationLogVo";
import Permission from "@/views/components/Permission";
import { MyTaskTradeStats, loadMyTaskTradeStats, type MyTaskTradeStat } from "./MyTaskTradeStats";
import { MyTaskFiscalStats, loadMyTaskFiscalStats, type MyTaskFiscalStat } from "./MyTaskFiscalStats";
import { MyTaskLogisticsStats, loadMyTaskLogisticsStats, type MyTaskLogisticsStat } from "./MyTaskLogisticsStats";
import {
    MyTaskLogisticsExtraStats,
    loadMyTaskLogisticsExtraStats,
    type MyTaskLogisticsExtraStat
} from "./MyTaskLogisticsExtraStats";
import { MyTaskIndexStats, loadMyTaskIndexStats, type MyTaskIndexStat } from "./MyTaskIndexStats";
import { MyTaskOtherStats, loadMyTaskOtherStats, type MyTaskOtherStat } from "./MyTaskOtherStats";
import { getMyTaskNewsNavigation, prepareMyTaskNewsNavigation } from "./MyTaskAction";
import "./common/index.scss";

/** 对齐 Vue myTaskStatistics 的分组权限，避免无权分组触发请求和渲染。 */
const MY_TASK_TRADE_PERMISSIONS = ["ROLE_PRODUCT_MANAGE", "ROLE_APPLY_SAMPLE_DEAL", "ROLE_REFUND_MANAGE"];
// 旧 Vue 财税分组还受船运定金权限控制，即使该权限没有独立统计项也必须保留分组入口。
const MY_TASK_FISCAL_PERMISSIONS = ["ROLE_SHIP_ORDER_SERVICE_FEE_W", "ROLE_SHIP_DOWNPAYMENT_W", "ROLE_REFUELING_FEE_W"];
const MY_TASK_LOGISTICS_PERMISSIONS = ["ROLE_SHIP_DEMAND_DEAL", "ROLE_SHIP_DEMAND_MANAGE", "ROLE_GAS_ORDER_DEAL"];
const MY_TASK_LOGISTICS_EXTRA_PERMISSIONS = [
    "ROLE_DEVICE_W",
    "ROLE_DEVICE_BIND_W",
    "ROLE_SHIP_CLUE_W",
    "ROLE_SHIP_REVIEW",
    "ROLE_WARNING_MANAGE",
    "ROLE_WARNING_DEAL",
    "ROLE_MODEL_DATA_W"
];
const MY_TASK_INDEX_PERMISSIONS = [
    "ROLE_CATEGORY_INDEX_DEAL",
    "ROLE_CATEGORY_INDEX_MANAGE",
    "ROLE_COMPSITE_INDEX_MANAGE",
    "ROLE_FREIGHT_RATE_DEAL",
    "ROLE_FREIGHT_RATE_MANAGE",
    "ROLE_COMPSITE_FREIGHT_MANAGE",
    "ROLE_INFO_W",
    "ROLE_SAND_ACADEMY_W",
    "ROLE_PETROL_INDEX_MANAGE"
];
const MY_TASK_OTHER_PERMISSIONS = [
    "ROLE_ORG_REVIEW",
    "ROLE_SS_FEEDBACK_W",
    "ROLE_FUNCTION_W",
    "ROLE_SS_HELP_W",
    "ROLE_ASSISTANTS_CORPUS_W",
    "ROLE_FUNDS_W"
];

interface OperationLogPageResult {
    list?: OperationLogVo[];
    total?: number;
}

export interface MyTaskPageProps {
    loadOtherStats?: () => Promise<MyTaskOtherStat[]>;
    loadIndexStats?: () => Promise<MyTaskIndexStat[]>;
    loadLogisticsExtraStats?: () => Promise<MyTaskLogisticsExtraStat[]>;
    loadLogisticsStats?: () => Promise<MyTaskLogisticsStat[]>;
    loadFiscalStats?: () => Promise<MyTaskFiscalStat[]>;
    loadTradeStats?: () => Promise<MyTaskTradeStat[]>;
    loadNews?: (params: {
        page: number;
        size: number;
        startTime: string;
        endTime: string;
    }) => Promise<OperationLogPageResult>;
    loadNewsDetail?: (params: { id: string; module: string }) => Promise<boolean>;
}

/** 复用 Vue 待办首页的操作日志分页接口。 */
export async function loadMyTaskNews(params: { page: number; size: number; startTime: string; endTime: string }) {
    const response = await request.service.get("/admin/operationLog/paging", { params });
    return response.data as OperationLogPageResult;
}

/** 复用 Vue 高亮动态跳转前的数据存在性校验接口。 */
export async function loadMyTaskNewsDetail(params: { id: string; module: string }) {
    const response = await request.service.get("/admin/operationLog/findByIdAndModule", { params });
    return Boolean(response.data);
}

/** 生成 Vue 版本当天首屏使用的时间范围。 */
export const getTodayTimeRange = (now = new Date()) => {
    const date = (value: Date) => {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };
    const today = date(now);
    return {
        startTime: `${today} 00:00:00`,
        endTime: `${today} 23:59:59`
    };
};

/** 去除 Vue 操作日志内容中的日期时间前缀。 */
const getNewsContent = (content?: string) =>
    content?.replace(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}\s+/, "").trim() || "暂无数据";

/** 我的待办首页首屏，保留 Vue 的统计、最新动态和消息提醒区块。 */
export function MyTaskPage({
    loadOtherStats = loadMyTaskOtherStats,
    loadIndexStats = loadMyTaskIndexStats,
    loadLogisticsExtraStats = loadMyTaskLogisticsExtraStats,
    loadLogisticsStats = loadMyTaskLogisticsStats,
    loadFiscalStats = loadMyTaskFiscalStats,
    loadTradeStats = loadMyTaskTradeStats,
    loadNews = loadMyTaskNews,
    loadNewsDetail = loadMyTaskNewsDetail
}: MyTaskPageProps) {
    const navigate = useNavigate();
    const [news, setNews] = useState<OperationLogVo[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [newsActionError, setNewsActionError] = useState("");

    /** 沿用 Vue：非删除日志先校验详情，存在后才跳转；失败时保持当前页。 */
    const handleNewsHighlight = async (item: OperationLogVo) => {
        const operation = item.operationLog;
        const target = getMyTaskNewsNavigation(operation);
        if (!target || !operation.highLight || !operation.bizNo || operation.operationModule === undefined) return;
        setNewsActionError("");
        try {
            if (operation.operationType !== 3) {
                const exists = await loadNewsDetail({ id: operation.bizNo, module: String(operation.operationModule) });
                if (!exists) {
                    setNewsActionError("该数据不存在");
                    return;
                }
            }
            prepareMyTaskNewsNavigation(operation);
            await navigate(target.path);
        } catch {
            setNewsActionError("该数据不存在");
        }
    };

    useEffect(() => {
        let disposed = false;
        const timeRange = getTodayTimeRange();
        void loadNews({ page: 1, size: 10, ...timeRange })
            .then(result => {
                if (disposed) return;
                setNews(result.list || []);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("最新动态加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadNews]);

    return (
        <section aria-label="my-task-page" className="my-task-page">
            <div className="left-panel" data-testid="my-task-left-panel">
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <div className="card-title">我的待办统计</div>
                <Permission anyOf={MY_TASK_TRADE_PERMISSIONS}>
                    <MyTaskTradeStats loadStats={loadTradeStats} />
                </Permission>
                <Permission anyOf={MY_TASK_FISCAL_PERMISSIONS}>
                    <MyTaskFiscalStats loadStats={loadFiscalStats} />
                </Permission>
                <Permission anyOf={MY_TASK_LOGISTICS_PERMISSIONS}>
                    <MyTaskLogisticsStats loadStats={loadLogisticsStats} />
                </Permission>
                <Permission anyOf={MY_TASK_LOGISTICS_EXTRA_PERMISSIONS}>
                    <MyTaskLogisticsExtraStats loadStats={loadLogisticsExtraStats} />
                </Permission>
                <Permission anyOf={MY_TASK_INDEX_PERMISSIONS}>
                    <MyTaskIndexStats loadStats={loadIndexStats} />
                </Permission>
                <Permission anyOf={MY_TASK_OTHER_PERMISSIONS}>
                    <MyTaskOtherStats loadStats={loadOtherStats} />
                </Permission>
            </div>
            <div className="right-panel" data-testid="my-task-right-panel">
                <Card
                    className="my-task-card"
                    title={
                        <Space className="w-full justify-between">
                            <span>最新动态</span>
                            <Button aria-label="更多" type="link" onClick={() => void navigate("/myTask/newsList")}>
                                更多
                            </Button>
                        </Space>
                    }
                >
                    {newsActionError ? <Alert message={newsActionError} role="alert" type="error" /> : null}
                    {news.length ? (
                        <Space direction="vertical" size="middle" className="w-full">
                            {news.map((item, index) => {
                                const content = item.operationLog?.content;
                                const time = content?.split(" ")[1] || "--:--";
                                const highlight = item.operationLog?.highLight;
                                const contentWithoutDate = getNewsContent(content);
                                const highlightIndex = highlight ? contentWithoutDate.indexOf(highlight) : -1;
                                const canNavigate =
                                    highlightIndex >= 0 && Boolean(getMyTaskNewsNavigation(item.operationLog));
                                return (
                                    <Space key={`${content || "news"}-${index}`} align="start" className="w-full">
                                        <Typography.Text type="secondary">{time}</Typography.Text>
                                        <Typography.Text>
                                            {canNavigate ? (
                                                <>
                                                    {contentWithoutDate.slice(0, highlightIndex)}
                                                    <Button
                                                        aria-label={`查看动态：${highlight}`}
                                                        className="h-auto p-0"
                                                        type="link"
                                                        onClick={() => void handleNewsHighlight(item)}
                                                    >
                                                        {highlight}
                                                    </Button>
                                                    {contentWithoutDate.slice(highlightIndex + highlight!.length)}
                                                </>
                                            ) : (
                                                contentWithoutDate
                                            )}
                                        </Typography.Text>
                                    </Space>
                                );
                            })}
                        </Space>
                    ) : (
                        <Empty description="暂无数据" />
                    )}
                </Card>
                <Card className="my-task-card" title="消息提醒">
                    <Empty description="暂无数据" />
                </Card>
            </div>
        </section>
    );
}
